import React from 'react';

import { Grid, Typography, useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import EditorialBand from '../../components/public/EditorialBand.component';
import EditorialCardGrid from '../../components/public/EditorialCardGrid.component';
import EditorialHero from '../../components/public/EditorialHero.component';
import {
  ZERO_WASTE_CALL,
  ZERO_WASTE_DISPOSALS,
  ZERO_WASTE_HERO,
  ZERO_WASTE_PACKAGINGS,
} from '../../constants/zero-waste-content.constants';
import Meta from '../../../utils/metaDescription';

const useStyles = makeStyles((theme) => ({
  seriesTitle: {
    fontFamily: 'Bitter, serif',
    fontWeight: 700,
    fontSize: 'clamp(1.25rem, 3vw, 1.6rem)',
    lineHeight: 1.2,
    marginBottom: '1.25rem',
    color: theme.palette.client.primary.extraDark,
  },
  series: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3rem',
  },
  // Purement décoratifs, les fruits n'existent qu'à partir de `sm`, où ils encadrent le message.
  fruit: {
    display: 'block',
    width: '100%',
    maxWidth: 200,
    margin: '0 auto',
    // Le décalage passe par `transform` et non par une marge négative : il ne retire aucune
    // hauteur à la colonne, donc le texte central ne remonte pas pour compenser.
    position: 'relative',
    // Les fruits débordent sur les bandes voisines ; sans contexte d'empilement, le fond de la
    // bande suivante viendrait les recouvrir.
    zIndex: 1,
  },
  // En quinconce comme dans le legacy, mais seulement quand les fruits encadrent le message :
  // empilés sur mobile, un décalage vertical les ferait chevaucher le texte.
  fruitRaised: {
    [theme.breakpoints.up('sm')]: {
      transform: 'translateY(-60%)',
    },
  },
  fruitLowered: {
    [theme.breakpoints.up('sm')]: {
      transform: 'translateY(90%)',
    },
  },
  callText: {
    fontSize: '1rem',
    lineHeight: 1.65,
    color: '#fff',
    textAlign: 'center',
  },
  callHighlight: {
    marginTop: '1rem',
    fontFamily: 'Bitter, serif',
    fontWeight: 700,
    fontSize: 'clamp(1.15rem, 3vw, 1.6rem)',
    lineHeight: 1.3,
    color: '#fff',
    textAlign: 'center',
  },
}));

const ZeroWasteView: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();

  // Sortis du rendu plutôt que masqués : une image en `display: none` reste dans le DOM et peut
  // être téléchargée pour rien sur la connexion la plus contrainte.
  const showFruits = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <React.Fragment>
      <Meta page="dechet" />

      <EditorialBand ground="cream" underFloatingNav>
        <EditorialHero
          title={ZERO_WASTE_HERO.title}
          lead={ZERO_WASTE_HERO.lead}
          highlight={ZERO_WASTE_HERO.highlight}
        />
      </EditorialBand>

      <EditorialBand ground="plain">
        <EditorialCardGrid cards={ZERO_WASTE_PACKAGINGS} columns={3} />
      </EditorialBand>

      <EditorialBand ground="solid">
        <Grid container spacing={3} alignItems="center">
          {showFruits && (
            <Grid item sm={2}>
              <img
                className={`${classes.fruit} ${classes.fruitRaised}`}
                src={ZERO_WASTE_CALL.leftIllustration}
                alt={ZERO_WASTE_CALL.leftIllustrationAlt}
                loading="lazy"
              />
            </Grid>
          )}

          <Grid item xs={12} sm={8} md={8}>
            <Typography component="p" className={classes.callHighlight}>
              {ZERO_WASTE_CALL.highlight}
            </Typography>
            <Typography className={classes.callText}>{ZERO_WASTE_CALL.text}</Typography>
          </Grid>

          {showFruits && (
            <Grid item sm={2}>
              <img
                className={`${classes.fruit} ${classes.fruitLowered}`}
                src={ZERO_WASTE_CALL.rightIllustration}
                alt={ZERO_WASTE_CALL.rightIllustrationAlt}
                loading="lazy"
              />
            </Grid>
          )}
        </Grid>
      </EditorialBand>

      <EditorialBand ground="tint">
        <div className={classes.series}>
          {ZERO_WASTE_DISPOSALS.map((disposal) => (
            <section key={disposal.title}>
              <Typography component="h2" className={classes.seriesTitle}>
                {disposal.title}
              </Typography>
              <EditorialCardGrid cards={disposal.cases} columns={disposal.cases.length === 3 ? 3 : 2} />
            </section>
          ))}
        </div>
      </EditorialBand>
    </React.Fragment>
  );
};

export default ZeroWasteView;
